/** «Así se arma» en 3D (snippets/build-view.liquid).
 * - El póster (armada.png + miniatura inline) se ve desde el primer frame; <model-viewer> se inyecta diferido solo si hay
 *   WebGL y no hay prefers-reduced-motion, y el póster se desvanece cuando el modelo está cargado. Desktop (ADR 0016): con la
 *   primera interacción (mouse, scroll, teclado, toque) o con el visor en pantalla y 4 s de inactividad, lo que pase primero.
 *   Con render por software (SwiftShader, llvmpipe: sin GPU, como PageSpeed) o ≤ 2 núcleos se queda en el póster con el
 *   botón «Ver en 3D». Celular o red limitada: espera el toque (ADR 0013). El auto-rotate da una vuelta y se detiene; fuera
 *   de pantalla o con la pestaña oculta no gira (model-viewer no pide frames con la escena quieta).
 * - Switch «Armada | Piezas»: fija el estado objetivo. Con 3D reproduce «Piezas» o «Armar» a mano: currentTime = 0 (o el
 *   tiempo invertido si venía a mitad de la otra), play(), y al llegar al final pause() + currentTime = duración − 0,001
 *   para que quede quieta en la pose final (play({repetitions: 1}) seguía en loop). Sin 3D alterna las imágenes
 *   armada/explotada. Tocar rápido N veces termina siempre en el estado del switch (cada toque invalida el anterior).
 * - Variantes de material (KHR_materials_variants, góndolas Negro/Blanco): setVariant(name) cambia variant-name del visor sin
 *   recargar el GLB (el AR usa la variante activa) y los pósters armada/explotada por los de ese color (data-posters, un
 *   PNG por color renderizado en la órbita del visor). Cuarta etiqueta (góndolas: base con zócalo): hotspot-4 y el ítem 4 de
 *   la leyenda, ocultos cuando el producto no la trae.
 * - load(model): cambia de producto (selector del hero, <build-picker>): pausa el visor, cancela la demo y la animación en
 *   curso, vuelve a mostrar el póster del modelo nuevo con el fundido corto del CSS (sin hueco), cambia el src del visor,
 *   actualiza etiquetas, pastilla de carga, posiciones y órbitas, y cuando el GLB carga salta a la pose del switch (Armada o Piezas) antes de
 *   revelarse. Si la demo no había terminado y el usuario no tocó el switch, el switch vuelve a «Armada» (el final de la
 *   demo) y la demo no se repite. Solo se descarga el GLB del producto activo: el de otro chip, recién al elegirlo.
 * - Pose al cargar (onModelLoad): model-viewer, al cargar un src nuevo, vuelve a reproducir la animationName vigente desde
 *   el tiempo 0 (con «Armar» eso es la pose desarmada, y si venía reproduciendo sigue en loop), así que SIEMPRE se fija la
 *   pose del switch con jumpTo (pause + animationName + currentTime = duración − 0,001) antes de quitar el póster.
 * - Cotas de medida (placeDims / drawDims): solo en «Armada» y con 3D. Ancho en la base del frente, alto por el parante
 *   delantero izquierdo y fondo en la base del lateral derecho, 7 cm por fuera de los bounds reales del modelo
 *   (getDimensions / getBoundingBoxCenter). Los extremos son hotspots de model-viewer creados acá; las líneas SVG, los remates
 *   y la etiqueta se recalculan por frame desde queryHotspot (como el ejemplo de dimensiones de model-viewer: camera-change
 *   no se dispara con auto-rotate). Una cota se apaga cuando su cara no mira a la cámara: se calcula acá con la órbita y el
 *   giro de la mesa (el facingCamera de model-viewer queda un frame viejo cuando la escena deja de renderizar). Los textos
 *   salen de los metafields de medidas (sin dato, esa cota no existe). Sin 3D (póster) no hay cotas.
 * - Extras del GLB (ADR 0012): el pipeline scripts/3d escribe asset.extras.organizate (hotspots en metros, offsets, medidas)
 *   en cada GLB. Si Liquid no trajo posiciones explícitas (data-hotspots-auto / model.hotspotsAuto), readExtras lee el
 *   trozo JSON del GLB (segundo fetch, servido del caché HTTP) y coloca las etiquetas desde ahí. */
const END_GAP = 0.001;
/** Cambia un póster: con una URL del CDN de imágenes (width=) arma el srcset (480-1600 px); si no, solo src. */
function setPoster(img, url) {
  if (!img || !url) return;
  delete img.dataset.src;
  delete img.dataset.srcset;
  if (/[?&]width=\d+/.test(url)) {
    img.srcset = [480, 720, 960, 1280, 1600].map((w) => `${url.replace(/([?&]width=)\d+/, `$1${w}`)} ${w}w`).join(', ');
  } else img.removeAttribute('srcset');
  if (img.getAttribute('src') !== url) img.src = url;
}
/** Póster explotada diferido (data-src/data-srcset, ADR 0016): se pide al pasar a «Piezas» o en inactividad tras load. */
function wakeExploded(root) {
  const img = root.querySelector('.build__static--explotada[data-src]');
  if (!img) return;
  if (img.dataset.srcset) img.srcset = img.dataset.srcset;
  img.src = img.dataset.src;
  delete img.dataset.src;
  delete img.dataset.srcset;
}
let viewerScript = null;
const extrasCache = new Map();

/** Trozo JSON de un GLB → extras «organizate» (asset.extras o scene.extras) o null. */
function readExtras(src) {
  if (!src) return Promise.resolve(null);
  if (!extrasCache.has(src)) {
    extrasCache.set(src, fetch(src, { mode: 'cors', credentials: 'same-origin' }).then((r) => r.arrayBuffer()).then((buf) => {
      const dv = new DataView(buf);
      if (buf.byteLength < 20 || dv.getUint32(0, true) !== 0x46546c67) return null;
      const len = dv.getUint32(12, true);
      const json = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 20, len)));
      return json.asset?.extras?.organizate || json.scenes?.[0]?.extras?.organizate || null;
    }).catch(() => null));
  }
  return extrasCache.get(src);
}

/** WebGL y si renderiza por software (SwiftShader, llvmpipe, «Software»: sin GPU; ahí model-viewer cuesta decenas de
 * segundos de CPU). Crear el contexto cuesta en el hilo principal: se llama recién al arrancar el 3D (ADR 0013). */
function webglInfo() {
  try {
    if (!window.WebGLRenderingContext) return { ok: false };
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) return { ok: false };
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = String((ext && gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) || gl.getParameter(gl.RENDERER) || '');
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return { ok: true, software: /swiftshader|llvmpipe|software|basic render/i.test(renderer) };
  } catch { return { ok: false }; }
}

/** Equipo que no banca el 3D: render por software o 2 núcleos o menos. localStorage build-3d=force lo saltea (e2e). */
function weakGraphics(info) {
  try { if (localStorage.getItem('build-3d') === 'force') return false; } catch { /* sin storage */ }
  return info.software || (navigator.hardwareConcurrency || 8) <= 2;
}

/** Conexión o dispositivo limitado (ADR 0013): touch sin hover fino, Ahorro de datos o red 2g/3g. Ahí el 3D espera un toque. */
function constrainedDevice() {
  const c = navigator.connection;
  return matchMedia('(hover: none) and (pointer: coarse)').matches || Boolean(c && (c.saveData || /2g|3g/.test(c.effectiveType || '')));
}

/** El decoder Draco (lo pide model-viewer 3.5.0 al primer GLB comprimido) se precarga recién cuando se va a cargar el 3D. */
let dracoQueued = false;
function preloadDraco() {
  if (dracoQueued) return;
  dracoQueued = true;
  const base = 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/';
  const add = (attrs) => { const link = document.createElement('link'); Object.assign(link, attrs); document.head.append(link); };
  add({ rel: 'preconnect', href: 'https://www.gstatic.com', crossOrigin: 'anonymous' });
  for (const file of ['draco_wasm_wrapper.js', 'draco_decoder.wasm']) add({ rel: 'preload', as: 'fetch', href: base + file, crossOrigin: 'anonymous' });
}

/** Inyecta model-viewer una sola vez por documento; resuelve true cuando el custom element está definido. */
function loadViewer(src) {
  if (customElements.get('model-viewer')) return Promise.resolve(true);
  if (!src) return Promise.resolve(false);
  viewerScript ||= new Promise((resolve) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    script.crossOrigin = 'anonymous';
    script.addEventListener('load', () => resolve(true), { once: true });
    script.addEventListener('error', () => resolve(false), { once: true });
    document.head.append(script);
  });
  return viewerScript.then((ok) => (ok ? customElements.whenDefined('model-viewer').then(() => true) : false));
}

class BuildView extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = matchMedia('(max-width: 767px)');
    this.desktop = matchMedia('(min-width: 1024px)');
    this.toggle = this.querySelector('[data-build-toggle]');
    this.options = [...this.querySelectorAll('[data-build-option]')];
    this.viewer = this.querySelector('model-viewer');
    this.dims = this.querySelector('[data-build-dims]');
    try { this.posters = JSON.parse(this.dataset.posters || 'null'); } catch { this.posters = null; }
    this.dimKeys = this.dims ? [...this.dims.querySelectorAll('[data-dim-label]:not([hidden])')].map((el) => el.dataset.dimLabel) : [];
    this.arNote = this.closest('.product__gallery')?.querySelector('[data-build-ar-note]') || null;
    this.exploded = false;
    this.token = 0;
    this.hotspotsAuto = this.hasAttribute('data-hotspots-auto');
    this.classList.add('build--js');
    this.watchPoster();
    // La explotada se precarga en inactividad después de load (nunca antes del LCP); al tocar «Piezas», en el momento.
    const idleExploded = () => { if ('requestIdleCallback' in window) requestIdleCallback(() => wakeExploded(this), { timeout: 4000 }); else setTimeout(() => wakeExploded(this), 1500); };
    if (document.readyState === 'complete') idleExploded(); else window.addEventListener('load', idleExploded, { once: true, signal });
    if (this.toggle) {
      this.toggle.hidden = false;
      this.toggle.addEventListener('change', (event) => {
        if (event.target.matches('[data-build-option]')) this.set(event.target.value === 'piezas', true);
      }, { signal });
    }
    this.reduced.addEventListener('change', () => { if (this.reduced.matches) this.stop3d(); }, { signal });
    // WebGL se chequea recién al arrancar el 3D (crear un contexto cuesta en el hilo principal: no va en la carga, ADR 0013).
    if (!this.viewer || this.reduced.matches) { this.classList.add('build--static'); return; }
    // Las cotas se redibujan por frame solo con el visor en pantalla y la pestaña visible.
    this.visible = true;
    if ('IntersectionObserver' in window) {
      this.visibility = new IntersectionObserver((entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting);
        this.syncRotate();
        if (this.visible && !this.dimFrame) this.drawDims();
      });
      this.visibility.observe(this);
    }
    document.addEventListener('visibilitychange', () => {
      this.syncRotate();
      if (!document.hidden && !this.dimFrame) this.drawDims();
    }, { signal });
    this.startButton = this.querySelector('[data-build-start]');
    let started = false;
    const load3d = () => {
      preloadDraco();
      loadViewer(this.dataset.viewerSrc).then((ok) => { if (this.isConnected) { if (ok) this.init3d(); else this.classList.add('build--static'); } });
    };
    // Espera: póster y botón (celular: «Tocá para ver en 3D»; desktop sin GPU: «Ver en 3D»). El botón o tocar el póster arranca.
    const wait = (label) => {
      this.classList.add('build--waiting');
      if (!this.startButton) return;
      const text = this.startButton.querySelector('[data-build-start-label]');
      if (label && text) text.textContent = label;
      this.startButton.hidden = false;
    };
    const userStart = () => {
      if (started === 'loading' || !this.isConnected || this.reduced.matches) return;
      started = 'loading';
      this.classList.remove('build--waiting');
      if (this.startButton) this.startButton.hidden = true;
      if (!webglInfo().ok) { this.classList.add('build--static'); return; }
      load3d();
    };
    const begin = () => {
      if (started || !this.isConnected || this.reduced.matches) return;
      started = true;
      const info = webglInfo();
      if (!info.ok) { this.classList.add('build--static'); return; }
      if (weakGraphics(info)) { wait(this.startButton?.dataset.labelDesktop); return; }
      started = 'loading';
      load3d();
    };
    this.begin = userStart;
    this.startButton?.addEventListener('click', userStart, { signal });
    this.querySelector('.build__canvas')?.addEventListener('pointerdown', (event) => {
      if (this.classList.contains('build--waiting') && !event.target.closest('.build__switch, .build__ar, [data-build-swatches], [data-build-start]')) userStart();
    }, { signal });
    if (constrainedDevice()) {
      // Celular o red limitada: el póster queda y el 3D arranca con el primer toque (botón «Tocá para ver en 3D» o el póster).
      wait();
      return;
    }
    // Desktop (ADR 0016): primera interacción o visor en pantalla + 4 s de inactividad, lo que pase primero. Hasta entonces, póster.
    let near = false;
    let interacted = false;
    let idleTimer = 0;
    const kick = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(begin, { timeout: 1000 }); else setTimeout(begin, 0);
    };
    // scroll solo del documento (sin capture): el de carriles y carruseles internos puede ser programático.
    const events = ['pointermove', 'pointerdown', 'wheel', 'keydown', 'touchstart'];
    const onInteract = () => {
      if (interacted) return;
      interacted = true;
      events.forEach((type) => window.removeEventListener(type, onInteract, { capture: true }));
      window.removeEventListener('scroll', onInteract);
      if (near) { clearTimeout(idleTimer); kick(); }
    };
    events.forEach((type) => window.addEventListener(type, onInteract, { capture: true, passive: true, signal }));
    window.addEventListener('scroll', onInteract, { passive: true, signal });
    const onNear = () => {
      near = true;
      if (interacted) kick();
      else idleTimer = setTimeout(kick, 4000);
    };
    signal.addEventListener('abort', () => clearTimeout(idleTimer));
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        onNear();
      }, { rootMargin: '200px' });
      io.observe(this);
      signal.addEventListener('abort', () => io.disconnect());
    } else onNear();
  }

  disconnectedCallback() {
    this.visibility?.disconnect();
    this.visibility = null;
    clearInterval(this.turnTimer);
    this.turnTimer = 0;
    clearTimeout(this.timer);
    clearTimeout(this.arTimer);
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.dimFrame);
    this.token += 1;
    this.controller?.abort();
    this.controller = null;
  }

  /** La miniatura inline se retira apenas la armada está decodificada. */
  watchPoster() {
    const armada = this.querySelector('.build__static--armada');
    const done = () => this.classList.add('build--loaded');
    if (!armada) return;
    if (armada.complete && armada.naturalWidth) done();
    else (armada.decode ? armada.decode() : Promise.reject()).then(done, () => armada.addEventListener('load', done, { once: true }));
  }

  init3d() {
    const viewer = this.viewer;
    const { signal } = this.controller;
    this.applyOrbit();
    this.mobile.addEventListener('change', () => this.applyOrbit(), { signal });
    this.desktop.addEventListener('change', () => this.applyOrbit(), { signal });
    viewer.addEventListener('error', () => this.stop3d(), { signal });
    viewer.addEventListener('camera-change', (event) => {
      if (event.detail?.source === 'user-interaction') this.classList.add('build--touched');
    }, { signal });
    viewer.addEventListener('load', () => this.onModelLoad(), { signal });
    if (viewer.loaded) this.onModelLoad();
  }

  /** Encuadre por tamaño y estado: órbita más lejana en mobile y, si hay data-orbit-exploded, en «Piezas» (la cámara se aleja y
   * vuelve al armar; model-viewer interpola el cambio). Si el usuario ya giró el modelo solo cambia el radio y su giro se
   * mantiene; antes de eso se fija la órbita completa (vuelve de frente, donde las etiquetas están pensadas) para no copiar
   * el vaivén del prompt de interacción. En desktop el objetivo corrido (hero) evita que la tarjeta tape el modelo. */
  applyOrbit() {
    const { dataset, viewer } = this;
    const base = this.mobile.matches ? dataset.orbitMobile : dataset.orbit;
    const far = this.mobile.matches ? dataset.orbitExplodedMobile || dataset.orbitExploded : dataset.orbitExploded;
    const orbit = this.exploded && far ? far : base;
    if (!orbit) return;
    if (this.ready3d && this.classList.contains('build--touched') && typeof viewer.getCameraOrbit === 'function') {
      const current = viewer.getCameraOrbit();
      const radius = orbit.trim().split(/\s+/)[2];
      if (radius) viewer.cameraOrbit = `${current.theta}rad ${current.phi}rad ${radius}`;
    } else if (this.ready3d) viewer.cameraOrbit = orbit;
    else if (viewer.getAttribute('camera-orbit') !== orbit) viewer.setAttribute('camera-orbit', orbit);
    const target = this.desktop.matches ? this.dataset.target : (this.mobile.matches ? this.dataset.targetMobile : null);
    if (target) this.viewer.setAttribute('camera-target', target);
    else this.viewer.removeAttribute('camera-target');
  }

  /** Modelo cargado (primero o cambio de producto): salta a la pose que marca el switch («Armar» o «Piezas» en su último
   * frame, pausada) antes de revelarse, sin reproducir nada. Sin esto, model-viewer deja el GLB nuevo en el frame 0 de la
   * animación vigente (desarmado) o reproduciéndola en loop. */
  async onModelLoad() {
    if (!this.isConnected || this.reduced.matches) return;
    const src = this.viewer.getAttribute('src');
    this.ready3d = true;
    this.animating = false;
    this.applyOrbit();
    this.placeDims();
    if (this.hotspotsAuto) await this.placeFromExtras(src);
    if (!this.ready3d || this.viewer.getAttribute('src') !== src) return;
    await this.jumpTo(this.exploded ? 'Piezas' : 'Armar');
    // Si mientras tanto se pidió otro modelo (load) o el usuario tocó el switch (animateTo), ese flujo sigue solo.
    if (!this.ready3d || this.viewer.getAttribute('src') !== src) return;
    this.classList.add('build--3d');
    this.watchTurn();
    // T-050: el giro se decide recién con el modelo listo; si mientras cargaba el visor salió de pantalla, no gira.
    this.syncRotate();
    this.updateArNote();
    this.arTimer = setTimeout(() => this.updateArNote(), 1500);
    this.dispatchEvent(new CustomEvent('build:loaded', { detail: { src: this.viewer.src } }));
    // Las cotas aparecen con el modelo quieto en «Armada»; si va a correr la demo, recién al terminar de armarse.
    if (this.dataset.userChoice || this.swapped) { this.drawDims(); return; }
    // Demo de entrada (solo el primer producto, una vez): separa las piezas y las vuelve a armar, salvo que el usuario
    // toque el switch o cambie de producto.
    this.timer = setTimeout(() => {
      this.set(true, false);
      this.timer = setTimeout(() => this.set(false, false), 2800);
    }, 500);
  }

  /** Auto-rotate: una vuelta completa y se detiene (antes giraba sin fin y model-viewer renderizaba cada frame). Suma el
   * giro de la mesa (turntableRotation) cada medio segundo; mientras el visor no se ve, no gira (syncRotate). */
  watchTurn() {
    if (this.turnTimer || this.turnDone || !this.viewer) return;
    let last = this.viewer.turntableRotation || 0;
    let total = 0;
    this.turnTimer = setInterval(() => {
      if (!this.isConnected || !this.viewer) { clearInterval(this.turnTimer); return; }
      const now = this.viewer.turntableRotation || 0;
      let delta = now - last;
      delta = Math.atan2(Math.sin(delta), Math.cos(delta));
      total += Math.abs(delta);
      last = now;
      if (total >= Math.PI * 2 - 0.02) {
        clearInterval(this.turnTimer);
        this.turnTimer = 0;
        this.turnDone = true;
        this.viewer.removeAttribute('auto-rotate');
      }
    }, 500);
  }

  /** Fuera de pantalla o con la pestaña oculta el modelo no gira: sin cambios en la escena, model-viewer no pide frames. */
  syncRotate() {
    if (!this.viewer || !this.ready3d || this.turnDone) return;
    const show = this.visible !== false && !document.hidden;
    if (show) this.viewer.setAttribute('auto-rotate', '');
    else this.viewer.removeAttribute('auto-rotate');
  }

  /** Oculta o muestra el visor (hero: producto sin GLB → panel de foto). Oculto, pausa la animación y cancela la demo; al
   * volver, el selector llama a load() y el modelo entra en la pose del switch. */
  setHidden(hidden) {
    this.hidden = Boolean(hidden);
    if (!hidden) return;
    clearTimeout(this.timer);
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.dimFrame);
    this.token += 1;
    this.animating = false;
    try { this.viewer?.pause?.(); } catch { /* visor sin definir */ }
  }

  /** Vuelve al póster y a las imágenes (error del visor o movimiento reducido activado después). */
  stop3d() {
    clearTimeout(this.timer);
    cancelAnimationFrame(this.frame);
    this.token += 1;
    this.ready3d = false;
    this.animating = false;
    this.classList.remove('build--3d');
    this.classList.add('build--static');
    this.drawDims();
    this.updateArNote();
    try { this.viewer?.pause?.(); this.viewer?.removeAttribute('auto-rotate'); } catch { /* visor sin definir */ }
  }

  /** Estado objetivo. Cada llamada invalida la animación anterior (token), así que N toques seguidos terminan en el último. */
  set(exploded, byUser) {
    clearTimeout(this.timer);
    if (byUser) this.dataset.userChoice = exploded ? 'piezas' : 'armada';
    this.options.forEach((input) => { input.checked = (input.value === 'piezas') === exploded; });
    if (exploded === this.exploded) return;
    if (exploded) wakeExploded(this);
    this.exploded = exploded;
    this.classList.toggle('build--exploded', exploded);
    // Las cotas se ocultan apenas empieza a separarse; al armar vuelven cuando la animación termina (finish de animateTo).
    if (exploded) this.drawDims();
    if (this.viewer && this.dataset.orbitExploded) this.applyOrbit();
    if (this.ready3d) this.animateTo(exploded ? 'Piezas' : 'Armar');
  }

  /** Cambia de producto: pósters, etiquetas, posiciones, órbitas y, con 3D, el src del visor. El póster del modelo nuevo
   * (en la pose del switch) tapa el visor con el fundido del CSS mientras carga el GLB; al cargar, onModelLoad lo revela
   * en esa misma pose. Sin 3D (póster + imágenes) solo cambian las imágenes. */
  load(model) {
    if (!model || !model.src) return;
    clearTimeout(this.timer);
    cancelAnimationFrame(this.frame);
    this.token += 1;
    this.swapped = true;
    this.animating = false;
    const armada = this.querySelector('.build__static--armada');
    const explotada = this.querySelector('.build__static--explotada');
    if (armada && model.poster && !model.posters) setPoster(armada, model.poster);
    if (explotada && model.posterExploded && !model.posters) setPoster(explotada, model.posterExploded);
    this.querySelector('.build__lqip')?.remove();
    const labels = model.labels || [];
    labels.forEach((label, i) => {
      this.querySelectorAll(`[data-build-label="${i}"], [data-build-legend="${i}"]`).forEach((el) => { el.textContent = label; });
      this.closest('.product__gallery')?.querySelectorAll(`[data-build-legend="${i}"]`).forEach((el) => { el.textContent = label; });
    });
    this.setFourthLabel(labels[3] || '');
    this.posters = model.posters || null;
    this.variant = model.variant || '';
    this.applyVariant();
    this.setLoad(model.load);
    this.setDims(model.dims);
    this.hotspotsAuto = Boolean(model.hotspotsAuto);
    this.setHotspots(model.hotspots || []);
    if (model.orbit) { this.dataset.orbit = model.orbit; this.dataset.orbitMobile = model.orbitMobile || model.orbit; }
    if (model.orbitExploded) { this.dataset.orbitExploded = model.orbitExploded; this.dataset.orbitExplodedMobile = model.orbitExplodedMobile || model.orbitExploded; }
    else { delete this.dataset.orbitExploded; delete this.dataset.orbitExplodedMobile; }
    this.classList.remove('build--touched');
    if (!this.viewer || this.classList.contains('build--static')) return;
    // Vuelve el póster (fundido de 450 ms) y recién entonces carga el modelo nuevo; onModelLoad lo revela en la pose actual.
    this.ready3d = false;
    this.classList.remove('build--3d');
    this.drawDims();
    this.updateArNote();
    // T-050: sin modelo listo syncRotate no actúa; se apaga acá para que un GLB pesado no siga «girando» fuera de pantalla
    // mientras carga (onModelLoad lo vuelve a sincronizar con la visibilidad).
    try { this.viewer.pause?.(); this.viewer.removeAttribute('auto-rotate'); } catch { /* visor sin definir */ }
    // Demo interrumpida (el usuario no tocó el switch): el switch vuelve a «Armada», que es donde terminaba la demo.
    if (!this.dataset.userChoice) this.set(false, false);
    // setAttribute sirve antes y después de que model-viewer esté definido (src es un atributo reflejado).
    if (this.viewer.getAttribute('src') !== model.src) this.viewer.setAttribute('src', model.src);
    else if (this.viewer.loaded) this.onModelLoad();
  }

  /** Posiciones «x y z» (con unidad, p. ej. «-0.96m 0.85m 0.03m») de las etiquetas 1, 1 espejada, 2, 3 y 4. */
  setHotspots(positions) {
    positions.forEach((position, i) => {
      const hot = this.querySelector(`[data-build-hot="${i}"]`);
      if (!hot || !position) return;
      hot.dataset.position = position;
      if (this.viewer?.updateHotspot) this.viewer.updateHotspot({ name: hot.getAttribute('slot'), position });
    });
  }

  /** Etiquetas desde los extras del GLB (hotspots en metros: 3 o 4 ternas; la 1 se repite espejada en x). */
  async placeFromExtras(src) {
    const extras = await readExtras(src);
    const list = Array.isArray(extras?.hotspots) ? extras.hotspots.filter((p) => Array.isArray(p) && p.length === 3) : [];
    if (!list.length) return;
    const fmt = (p) => p.map((v) => `${Number(v)}m`).join(' ');
    const [a, b, c, d] = list;
    const positions = [fmt(a), fmt([-a[0], a[1], a[2]]), b ? fmt(b) : '', c ? fmt(c) : '', d ? fmt(d) : ''];
    this.setHotspots(positions);
  }

  /** Cuarta etiqueta (hotspot-4 y el ítem 4 de la leyenda): visible solo con texto. */
  setFourthLabel(text) {
    const label = (text || '').trim();
    this.querySelectorAll('[data-build-hot="4"]').forEach((hot) => { hot.hidden = !label; });
    const scope = this.closest('.product__gallery') || this;
    scope.querySelectorAll('[data-build-legend-item="3"]').forEach((li) => { li.hidden = !label; });
    this.querySelectorAll('[data-build-legend-item="3"]').forEach((li) => { li.hidden = !label; });
  }

  /** Variante de material del GLB (Negro / Blanco): cambia variant-name sin recargar y los pósters de ese color. */
  setVariant(name) {
    this.variant = (name || '').trim();
    this.applyVariant();
  }

  applyVariant() {
    const { viewer, variant } = this;
    if (viewer) {
      if (variant) viewer.setAttribute('variant-name', variant); else viewer.removeAttribute('variant-name');
      if (this.ready3d && 'variantName' in viewer) viewer.variantName = variant || null;
    }
    const set = this.posters && variant ? this.posters[variant] : null;
    if (!set) return;
    const armada = this.querySelector('.build__static--armada');
    const explotada = this.querySelector('.build__static--explotada');
    // Sin atributo poster en model-viewer: su póster interno se pintaba tarde y quedaba como LCP; el propio (img) tapa el visor.
    if (set.armada) setPoster(armada, set.armada);
    if (set.explotada) setPoster(explotada, set.explotada);
  }

  /** Pastilla «N kg por estante» (fija, visible en las dos poses): texto del metafield del producto o nada. */
  setLoad(text) {
    const pill = this.querySelector('[data-build-load]');
    if (!pill) return;
    const label = (text || '').trim();
    pill.querySelector('[data-build-load-text]').textContent = label;
    pill.hidden = !label;
  }

  /** Textos de las cotas (ancho, alto, fondo: «200 cm»), del producto o la variante; vacío = esa cota no se dibuja. */
  setDims(list) {
    const { dims } = this;
    if (!dims) return;
    const texts = Array.isArray(list) ? list : [];
    this.dimKeys = [];
    ['w', 'h', 'd'].forEach((key, i) => {
      const text = (texts[i] || '').trim();
      const label = dims.querySelector(`[data-dim-label="${key}"]`);
      const group = dims.querySelector(`[data-dim="${key}"]`);
      if (label) { label.textContent = text; label.hidden = !text; }
      if (group) group.style.display = text ? '' : 'none';
      if (text) this.dimKeys.push(key);
    });
    this.drawDims();
  }

  /** Extremos de las cotas como hotspots (metros sobre los bounds reales del modelo cargado, 7 cm por fuera): ancho en la base
   * del frente (cara +z), alto por el parante delantero izquierdo (cara +z) y fondo en la base del lateral derecho (cara +x).
   * Se crean una vez y se reposicionan en cada modelo. */
  placeDims() {
    const { viewer, dims } = this;
    if (!dims || typeof viewer?.getDimensions !== 'function') return;
    const size = viewer.getDimensions();
    const c = viewer.getBoundingBoxCenter();
    if (!size || !c || !(size.x > 0)) return;
    const gap = 0.07;
    const x0 = c.x - size.x / 2; const x1 = c.x + size.x / 2;
    const y0 = c.y - size.y / 2; const y1 = c.y + size.y / 2;
    const z0 = c.z - size.z / 2; const z1 = c.z + size.z / 2;
    const cotas = {
      w: [[x0, y0 - gap, z1], [x1, y0 - gap, z1]],
      h: [[x0 - gap, y0, z1], [x0 - gap, y1, z1]],
      d: [[x1, y0 - gap, z1], [x1, y0 - gap, z0]],
    };
    for (const [key, [a, b]] of Object.entries(cotas)) {
      [a, b].forEach((point, i) => {
        const name = `hotspot-dim-${key}${i}`;
        let el = viewer.querySelector(`[slot="${name}"]`);
        if (!el) {
          el = document.createElement('span');
          el.slot = name;
          el.className = 'build__dim-pt';
          el.setAttribute('aria-hidden', 'true');
          viewer.append(el);
        }
        const position = point.map((v) => `${v.toFixed(3)}m`).join(' ');
        el.dataset.position = position;
        viewer.updateHotspot?.({ name, position });
      });
    }
  }

  /** Muestra u oculta las cotas y, mientras se ven, redibuja líneas, remates y etiquetas en cada frame desde la proyección de
   * los hotspots (solo toca el DOM cuando algo se movió). Visibles solo con 3D, en «Armada» y con algún texto. Cada cota se
   * apaga cuando su cara (frente +z, lateral derecho +x) queda de espaldas o casi de canto respecto de la cámara: dirección
   * de la cámara en el espacio del modelo = órbita menos el giro de la mesa (auto-rotate gira el modelo, no la cámara). */
  drawDims() {
    cancelAnimationFrame(this.dimFrame);
    const { viewer, dims } = this;
    if (!dims) return;
    const keys = this.dimKeys || [];
    const show = this.ready3d && !this.exploded && keys.length > 0 && this.classList.contains('build--3d') && typeof viewer?.queryHotspot === 'function';
    dims.hidden = !show;
    if (!show) return;
    const faces = { w: [0, 0, 1], h: [0, 0, 1], d: [1, 0, 0] };
    const parts = keys.map((key) => ({
      key,
      face: faces[key],
      group: dims.querySelector(`[data-dim="${key}"]`),
      label: dims.querySelector(`[data-dim-label="${key}"]`),
      line: dims.querySelector(`[data-dim="${key}"] .build__dim-line`),
      ticks: [...dims.querySelectorAll(`[data-dim="${key}"] .build__dim-tick`)],
      last: '',
    }));
    const tick = () => {
      if (!this.isConnected || !this.ready3d || this.exploded) return;
      // Fuera de pantalla o con la pestaña oculta no se redibuja (vuelve con el IntersectionObserver o visibilitychange).
      if (!this.visible || document.hidden) { this.dimFrame = 0; return; }
      const orbit = viewer.getCameraOrbit();
      const az = orbit.theta - (viewer.turntableRotation || 0);
      const dir = [Math.sin(orbit.phi) * Math.sin(az), Math.cos(orbit.phi), Math.sin(orbit.phi) * Math.cos(az)];
      for (const part of parts) {
        const a = viewer.queryHotspot(`hotspot-dim-${part.key}0`);
        const b = viewer.queryHotspot(`hotspot-dim-${part.key}1`);
        if (!a || !b || !part.line) continue;
        const ax = a.canvasPosition.x; const ay = a.canvasPosition.y; const bx = b.canvasPosition.x; const by = b.canvasPosition.y;
        const on = part.face[0] * dir[0] + part.face[1] * dir[1] + part.face[2] * dir[2] > 0.1;
        const frame = `${on ? 1 : 0}|${Math.round(ax)},${Math.round(ay)},${Math.round(bx)},${Math.round(by)}`;
        if (frame === part.last) continue;
        part.last = frame;
        const len = Math.hypot(bx - ax, by - ay) || 1;
        const nx = (-(by - ay) / len) * 4; const ny = ((bx - ax) / len) * 4;
        part.line.setAttribute('x1', ax.toFixed(1)); part.line.setAttribute('y1', ay.toFixed(1));
        part.line.setAttribute('x2', bx.toFixed(1)); part.line.setAttribute('y2', by.toFixed(1));
        [[ax, ay], [bx, by]].forEach(([x, y], i) => {
          const t = part.ticks[i];
          if (!t) return;
          t.setAttribute('x1', (x + nx).toFixed(1)); t.setAttribute('y1', (y + ny).toFixed(1));
          t.setAttribute('x2', (x - nx).toFixed(1)); t.setAttribute('y2', (y - ny).toFixed(1));
        });
        // En mobile las cotas de la base (ancho y fondo) van apenas por encima de su línea: así no tocan el aviso «Arrastrá para girar».
        const lift = this.mobile.matches && part.key !== 'h' ? 14 : 0;
        if (part.label) part.label.style.transform = `translate(${((ax + bx) / 2).toFixed(1)}px, ${((ay + by) / 2 - lift).toFixed(1)}px) translate(-50%, -50%)`;
        part.group?.classList.toggle('is-off', !on);
        part.label?.classList.toggle('is-off', !on);
      }
      this.dimFrame = requestAnimationFrame(tick);
    };
    tick();
  }

  /** Nota «Modelo a escala real…» de la ficha: solo donde model-viewer puede abrir AR (donde también muestra el botón del slot). */
  updateArNote() {
    const note = this.arNote;
    if (!note) return;
    note.hidden = !(this.ready3d && this.classList.contains('build--3d') && this.viewer?.canActivateAR === true);
  }

  /** Pose final de una animación sin reproducirla (modelo recién cargado: «Armar» o «Piezas» según el switch). Si el nombre
   * no cambia, model-viewer ya reinició esa animación al cargar el src y solo hay que pausar y fijar el tiempo. */
  async jumpTo(name) {
    const viewer = this.viewer;
    const token = ++this.token;
    viewer.pause();
    viewer.animationName = name;
    await viewer.updateComplete;
    if (token !== this.token) return;
    viewer.currentTime = Math.max(viewer.duration - END_GAP, 0);
  }

  async animateTo(name) {
    const viewer = this.viewer;
    const token = ++this.token;
    cancelAnimationFrame(this.frame);
    const duration = viewer.duration || 1.2;
    // A mitad de la animación contraria: arranca desde el tiempo equivalente invertido (duración − currentTime).
    let from = 0;
    if (this.animating && viewer.animationName && viewer.animationName !== name) {
      from = Math.min(Math.max(duration - viewer.currentTime, 0), duration);
    }
    this.animating = true;
    viewer.animationName = name;
    await viewer.updateComplete;
    if (token !== this.token) return;
    const end = Math.max((viewer.duration || duration) - END_GAP, 0);
    viewer.currentTime = from;
    viewer.play();
    const started = performance.now();
    const budget = Math.max(end - from, 0) * 1000;
    let last = from;
    const finish = () => {
      if (token !== this.token) return;
      viewer.pause();
      viewer.currentTime = end;
      this.animating = false;
      this.drawDims();
      this.dispatchEvent(new CustomEvent('build:settled', { detail: { exploded: this.exploded, animation: name } }));
    };
    const tick = () => {
      if (token !== this.token) return;
      const time = viewer.currentTime;
      const elapsed = performance.now() - started;
      // Termina por tiempo o apenas antes del final; si el mixer dio la vuelta (pestaña oculta), también termina.
      if (elapsed >= budget - 12 || time < last || time >= end - 0.034) finish();
      else { last = time; this.frame = requestAnimationFrame(tick); }
    };
    this.frame = requestAnimationFrame(tick);
  }
}
if (!customElements.get('build-view')) customElements.define('build-view', BuildView);

/** Selector de productos del hero: radiogroup de chips (data-hero-chip con data-model JSON). Al cambiar, carga el modelo en
 * el <build-view> de data-for, muestra la franja de precio de ese producto (data-hero-foot) y actualiza la bajada
 * (data-hero-text, salvo que el editor la haya fijado). Producto sin GLB (model.src vacío): oculta el visor y muestra el panel
 * de foto (data-hero-photo) con la foto de la variante (fundido de 300 ms) y swatches (model.variants) que cambian la foto y
 * la variante de la franja (id del quick-buy, precio, miniatura). Con más de 3 chips el carril scrollea: flechas
 * (data-chips-prev/next) solo cuando hace falta y el chip elegido siempre a la vista. No precarga los GLB de los otros chips
 * (ADR 0016): cada uno se descarga recién al elegirlo, con el póster tapando el visor mientras llega. */

class BuildPicker extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.chips = [...this.querySelectorAll('[data-hero-chip]')];
    this.group = this.querySelector('[data-hero-chips]');
    this.view = document.getElementById(this.dataset.for || '');
    this.stage = this.closest('[data-hero-media]') || this.parentElement;
    this.photo = this.stage?.querySelector('[data-hero-photo]') || null;
    this.text = this.closest('.hero')?.querySelector('[data-hero-text]:not([data-hero-text-fixed])');
    this.prev = this.querySelector('[data-chips-prev]');
    this.next = this.querySelector('[data-chips-next]');
    if (this.group) this.group.hidden = false;
    this.classList.add('hero__picker--js');
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-hero-chip]')) this.select(event.target);
    }, { signal });
    const onSwatch = (event) => { if (event.target.matches('[data-hero-swatch]')) this.applyVariant(event.target); };
    this.photo?.addEventListener('change', onSwatch, { signal });
    this.view?.addEventListener('change', onSwatch, { signal });
    // Carril de chips: flechas solo cuando el carril scrollea (más de 3 productos o pantalla angosta).
    const step = () => Math.max((this.group?.clientWidth || 0) * 0.6, 120);
    const smooth = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
    this.prev?.addEventListener('click', () => this.group?.scrollBy({ left: -step(), behavior: smooth() }), { signal });
    this.next?.addEventListener('click', () => this.group?.scrollBy({ left: step(), behavior: smooth() }), { signal });
    this.group?.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal });
    if ('ResizeObserver' in window && this.group) { this.resizer = new ResizeObserver(() => this.updateArrows()); this.resizer.observe(this.group); }
    this.updateArrows();
    const checked = this.chips.find((chip) => chip.checked);
    if (checked) this.reveal(checked);
    // Sin 3D de entrada (el primer producto no tiene GLB): swatches del producto activo.
    const model = checked ? this.model(checked) : null;
    if (model && (model.src || (this.photo && !this.photo.hidden))) this.renderSwatches(model, checked.value, false);
  }

  disconnectedCallback() { this.controller?.abort(); this.controller = null; this.resizer?.disconnect(); this.resizer = null; }

  model(chip) {
    try { return JSON.parse(chip.dataset.model || 'null'); } catch { return null; }
  }

  /** Flechas del carril: ocultas si todo entra; deshabilitadas en cada extremo. */
  updateArrows() {
    const { group, prev, next } = this;
    if (!group || !prev || !next) return;
    const scrollable = group.scrollWidth > group.clientWidth + 2;
    prev.hidden = !scrollable; next.hidden = !scrollable;
    this.classList.toggle('hero__picker--scroll', scrollable);
    prev.disabled = group.scrollLeft <= 2;
    next.disabled = group.scrollLeft + group.clientWidth >= group.scrollWidth - 2;
  }

  /** Lleva el chip elegido a la vista del carril (solo scroll horizontal del carril, nunca de la página). */
  reveal(chip) {
    const { group } = this;
    const label = chip.closest('label');
    if (!group || !label) return;
    const left = label.offsetLeft - group.offsetLeft;
    const right = left + label.offsetWidth;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (left < group.scrollLeft) group.scrollTo({ left: left - 4, behavior });
    else if (right > group.scrollLeft + group.clientWidth) group.scrollTo({ left: right - group.clientWidth + 4, behavior });
  }

  select(chip) {
    const model = this.model(chip);
    const index = chip.value;
    this.stage?.querySelectorAll('[data-hero-foot]').forEach((foot) => { foot.hidden = foot.dataset.heroFoot !== index; });
    if (this.text && model?.text) this.text.textContent = model.text;
    this.reveal(chip);
    if (model?.src) {
      // Producto con 3D: vuelve el visor (si estaba en foto) y carga su modelo; con variantes de color, swatches sobre el visor.
      if (this.photo) this.photo.hidden = true;
      if (this.view) {
        if (this.view.hidden) this.view.setHidden?.(false) ?? (this.view.hidden = false);
        this.view.load(model);
      }
      this.renderSwatches(model, index, false);
    } else if (model && this.photo) {
      // Producto sin 3D: panel de foto con la variante elegida y sus swatches.
      if (this.view) this.view.setHidden?.(true) ?? (this.view.hidden = true);
      this.photo.hidden = false;
      this.renderSwatches(model, index, true);
    }
    this.dispatchEvent(new CustomEvent('hero:product', { detail: { index, model }, bubbles: true }));
  }

  /** Swatches por variante (una por variante): sobre el visor 3D (cambian variant-name) o sobre la foto (cambian la foto).
   * La variante inicial es la primera disponible (o la que trae el modelo). */
  renderSwatches(model, index, fade) {
    const in3d = Boolean(model.src);
    const box = in3d ? this.view?.querySelector('[data-build-swatches]') : this.photo?.querySelector('[data-hero-swatches]');
    const other = in3d ? this.photo?.querySelector('[data-hero-swatches]') : this.view?.querySelector('[data-build-swatches]');
    if (other) { other.hidden = true; other.replaceChildren(); }
    const variants = Array.isArray(model.variants) ? model.variants : [];
    const first = variants.find((v) => v.title === model.variant) || variants.find((v) => v.available) || variants[0] || null;
    if (box) {
      box.replaceChildren(...variants.map((v) => {
        const label = document.createElement('label');
        label.className = `swatch${v.available ? '' : ' swatch--off'}`;
        const input = document.createElement('input');
        input.type = 'radio'; input.name = `hero-variant-${index}`; input.value = String(v.id);
        input.dataset.heroSwatch = ''; input.dataset.index = String(index);
        input.checked = v === first;
        const dot = document.createElement('span');
        dot.className = 'swatch__dot'; dot.setAttribute('aria-hidden', 'true');
        if (v.dot) dot.style.backgroundImage = `url(${v.dot})`;
        const name = document.createElement('span');
        name.className = 'swatch__name'; name.textContent = v.title;
        label.append(input, dot, name);
        return label;
      }));
      if (model.option) box.setAttribute('aria-label', model.option);
      box.hidden = variants.length < 2;
    }
    if (in3d) { if (first && this.view?.setVariant) this.view.setVariant(first.title); }
    else this.setPhoto(first?.image || model.photo?.src || '', model.photo?.alt || '', fade);
    if (first) this.updateFoot(index, first);
  }

  /** Cambia la foto del panel con un fundido de 300 ms (la nueva se decodifica antes de mostrarse: sin hueco). */
  setPhoto(src, alt, fade) {
    const img = this.photo?.querySelector('.hero__photo-img');
    if (!img || !src) return;
    const apply = () => { img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = src; img.alt = alt || ''; this.photo.classList.remove('is-fading'); };
    if (!fade || matchMedia('(prefers-reduced-motion: reduce)').matches || !img.getAttribute('src')) { apply(); return; }
    const token = (this.photoToken = (this.photoToken || 0) + 1);
    this.photo.classList.add('is-fading');
    const next = new Image();
    next.src = src;
    const done = () => { if (token === this.photoToken) setTimeout(apply, 300); };
    (next.decode ? next.decode() : Promise.resolve()).then(done, done);
  }

  applyVariant(input) {
    const chip = this.chips.find((c) => c.value === input.dataset.index) || this.chips.find((c) => c.checked);
    const model = chip ? this.model(chip) : null;
    const variant = (model?.variants || []).find((v) => String(v.id) === input.value);
    if (!variant) return;
    if (model.src) this.view?.setVariant?.(variant.title);
    else this.setPhoto(variant.image || model.photo?.src || '', model.photo?.alt || '', true);
    this.updateFoot(input.dataset.index, variant);
  }

  /** La franja del producto sigue a la variante: id del quick-buy, precio, miniatura y botón (Agotado si no hay stock). */
  updateFoot(index, variant) {
    const foot = this.stage?.querySelector(`[data-hero-foot="${index}"]`);
    if (!foot) return;
    const id = foot.querySelector('[data-hero-id]');
    if (id) id.value = String(variant.id);
    const price = foot.querySelector('[data-hero-price]');
    if (price && variant.price) price.textContent = variant.price;
    const transfer = foot.querySelector('[data-transfer-price]'); // T-041: precio por transferencia (pack de 1) de la variante
    if (transfer && variant.transfer) transfer.textContent = variant.transfer;
    const thumb = foot.querySelector('[data-hero-thumb] img');
    if (thumb && variant.thumb) { thumb.removeAttribute('srcset'); thumb.src = variant.thumb; }
    const button = foot.querySelector('[data-hero-add]');
    if (button) {
      const text = button.querySelector('[data-hero-add-text]');
      const quick = button.closest('quick-buy');
      if (text && !text.dataset.add) text.dataset.add = text.textContent;
      button.disabled = variant.available === false;
      if (text) text.textContent = variant.available === false ? quick?.dataset.soldOut || text.dataset.add : text.dataset.add;
    }
  }
}
if (!customElements.get('build-picker')) customElements.define('build-picker', BuildPicker);
